import { useState } from 'react'
import { Center, Stack, Text, Title, SimpleGrid, Button, UnstyledButton } from '@mantine/core'

/**
 * Full-screen role picker shown once at the start of a playthrough, before
 * the first beat, when the story has player roles enabled and the session
 * has no role yet. Resume never shows this; a new playthrough picks fresh.
 *
 * `takenRoleKeys` lists roles already claimed (or at cap) by teammates in a
 * team playthrough; those cards render disabled. The server still enforces
 * uniqueness on submit, so this is a hint, not the source of truth.
 */
export function RoleSelectScreen({ story, roles, onSelect, saving, takenRoleKeys = [] }) {
  const [selectedKey, setSelectedKey] = useState(null)
  const taken = new Set(takenRoleKeys)

  return (
    <Center style={{ minHeight: '100vh', background: '#060d18', padding: 24 }}>
      <Stack align="center" gap="xl" style={{ maxWidth: 920, width: '100%' }}>
        <Stack align="center" gap="xs">
          <Title order={2} c="cyan.3" ff="Orbitron, sans-serif" ta="center">
            Choose your role
          </Title>
          <Text c="dimmed" ta="center" ff="Rajdhani, sans-serif" size="lg">
            {story?.name ? `Your role shapes what you see in ${story.name}.` : 'Your role shapes what you see in this story.'}
            {' '}You'll keep it for this entire playthrough.
          </Text>
        </Stack>

        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md" style={{ width: '100%' }}>
          {roles.map((role) => {
            const isSelected = role.key === selectedKey
            const isTaken = taken.has(role.key)
            return (
              <UnstyledButton
                key={role.key}
                onClick={() => !isTaken && setSelectedKey(role.key)}
                aria-pressed={isSelected}
                aria-disabled={isTaken}
                disabled={isTaken}
                data-testid={`role-card-${role.key}`}
                style={{
                  background: isSelected ? 'rgba(0, 229, 255, 0.12)' : 'rgba(10, 22, 40, 0.8)',
                  border: `1px solid ${isSelected ? 'rgba(0, 229, 255, 0.7)' : 'rgba(0, 255, 255, 0.15)'}`,
                  borderRadius: 6,
                  padding: '16px 18px',
                  transition: 'border-color 0.15s ease, background 0.15s ease',
                  opacity: isTaken ? 0.45 : 1,
                  cursor: isTaken ? 'not-allowed' : 'pointer',
                }}
              >
                <Stack gap={6}>
                  <Text fw={700} c="cyan.3" ff="Orbitron, sans-serif" size="sm">
                    {role.name}
                  </Text>
                  <Text c="gray.4" ff="Rajdhani, sans-serif" size="sm" lh={1.45}>
                    {role.description}
                  </Text>
                  {isTaken && (
                    <Text c="orange.4" ff="Rajdhani, sans-serif" size="xs" fw={600}>
                      Taken by a teammate
                    </Text>
                  )}
                </Stack>
              </UnstyledButton>
            )
          })}
        </SimpleGrid>

        <Button
          size="md"
          color="cyan"
          variant={selectedKey ? 'filled' : 'default'}
          disabled={!selectedKey}
          loading={saving}
          onClick={() => onSelect(selectedKey)}
          ff="Orbitron, sans-serif"
          data-testid="confirm-role"
        >
          Begin as {roles.find((r) => r.key === selectedKey)?.name || '...'}
        </Button>
      </Stack>
    </Center>
  )
}
